import { uniq, upperFirst } from 'lodash-es';
import { nextTick } from 'vue';
import type {
  BasicColumn,
  BasicTableProps,
  FormTableAction,
  GetColumnsParams,
} from '@mts/components/Table';
import type { DeepPartial, EmitType, Nullable, Recordable } from '@mts-fe/types';
import { deepMerge, getRowSpanCount } from '@mts-fe/utils';
import { getDataIndex } from '@mts/components/Table/src/helper';
import type { DynamicOption, Fetchable, RenderTableColumn } from '..';
import { createSchemaApi, DICT_OPTION_API, getDictOptions } from '../api';

export const TABLE_EMIT_EVENTS = [
  'change',
  'fetch-success',
  'fetch-error',
  'fetch-done',
  'selection-change',
  'columns-change',
  'search-change',
  'focus-change',
  'edit-change',
  'card-click',
];
/** 透传表格事件 */
export const useTableEvents = (emit: EmitType, callback?: EmitType) =>
  TABLE_EMIT_EVENTS.reduce(
    (acc, eventKey) =>
      Object.assign(acc, {
        [`on${upperFirst(eventKey)}`]: (...args: Nullable<Recordable<any>>[]) =>
          eventKey === 'change' && typeof args[1] === 'object'
            ? emit('search-change', ...args)
            : (callback?.(eventKey, ...args), emit(eventKey, ...args)),
      }),
    {} as Recordable<Function>,
  );

const fetchMap = new Map<string, Promise<any>>();
/** 表格列枚举值加载：枚举映射渲染、列过滤 */
export const useColumnFetch = (
  columns: RenderTableColumn[] = [],
  tableAction: Pick<FormTableAction, 'getColumns' | 'setColumns'>,
) => {
  const fetchColumns: {
    type: 'render' | 'filter' | 'component';
    dataIndex: string;
    fetchKey: string;
  }[] = [];
  const getFetchKey = ({ api, params = {} }: Fetchable) => api + JSON.stringify(params);

  const doFetch = ({ api, params = {} }: Fetchable) => {
    const fetchKey = getFetchKey({ api, params });
    if (!fetchMap.has(fetchKey)) {
      const fetcher = api === DICT_OPTION_API ? getDictOptions : createSchemaApi(api);
      fetchMap.set(fetchKey, fetcher!(params));
    }
  };

  columns.forEach((column) => {
    if (column.render && 'api' in column.render && column.render.api) {
      fetchColumns.push({
        type: 'render',
        dataIndex: getDataIndex(column.dataIndex),
        fetchKey: getFetchKey(column.render),
      });
      doFetch(column.render);
    }
    if (column.filter?.api) {
      fetchColumns.push({
        type: 'filter',
        dataIndex: getDataIndex(column.dataIndex),
        fetchKey: getFetchKey(column.filter),
      });
      doFetch(column.filter);
    }
    if (
      (column.editComponent || column.showIn) &&
      column.editComponentProps &&
      'api' in column.editComponentProps &&
      column.editComponentProps.api
    ) {
      fetchColumns.push({
        type: 'component',
        dataIndex: getDataIndex(column.dataIndex),
        fetchKey: getFetchKey(column.editComponentProps),
      });
      doFetch(column.editComponentProps);
      delete column.editComponentProps.api;
    }
  });

  if (fetchMap.size === 0) return;
  Promise.allSettled(fetchMap.values()).then((results) => {
    const getResultData = (fetchKey: string) => {
      const res = results[[...fetchMap.keys()].findIndex((key) => key === fetchKey)];
      return res.status === 'fulfilled' ? res.value : void 0;
    };

    const _columns = tableAction.getColumns();
    const mergeColumn = (dataIndex: string, newCol: DeepPartial<RenderTableColumn>) => {
      const index = _columns.findIndex((i) => dataIndex === getDataIndex(i.dataIndex));
      if (index < 0) return;

      // 兼容接口返回非标准字段集
      const _props = _columns[index].editComponentProps;
      _props &&
        typeof _props !== 'function' &&
        (_props.valueField || _props.labelField || _props.keyField) &&
        newCol.editComponentProps &&
        (newCol.editComponentProps as Recordable).options.forEach((i) => {
          i.value = i[_props.valueField];
          i.label = i[_props.labelField];
          i.key = i[_props.keyField];
        });

      // @ts-ignore
      return _columns.splice(index, 1, deepMerge(_columns[index], newCol));
    };

    for (const column of fetchColumns) {
      const data = getResultData(column.fetchKey);
      column.type === 'render' && mergeColumn(column.dataIndex, { render: { options: data } });
      column.type === 'filter' &&
        mergeColumn(column.dataIndex, { filters: data?.map((i) => ({ ...i, text: i.label })) });
      column.type === 'component' &&
        data &&
        mergeColumn(column.dataIndex, { editComponentProps: { api: null, options: data } });
    }
    tableAction.setColumns?.(_columns, true);
  });
};

export const useRowSelectionCellMerge = (
  {
    mergeCellBy,
    ...defRowSelection
  }: BasicTableProps['rowSelection'] & { mergeCellBy?: string } = {},
  dataSource: Recordable[],
  tableAction: FormTableAction,
) => {
  if (!mergeCellBy) return;

  const uniqKeys = mergeCellBy?.split(',') || [];
  const getKeysValue = (data: Recordable) => uniqKeys.map((key) => data[key]).join('&');

  const rowSelection: BasicTableProps['rowSelection'] = {
    ...defRowSelection,
    renderCell: (value, record, index, originNode) => ({
      children: originNode,
      props: { rowSpan: getRowSpanCount(dataSource, uniqKeys, index) },
    }),
    onSelect: (record, selected, selectedRows) => {
      if (!selectedRows.length) return;

      const tableData = tableAction.getDataSource() || [];
      const recordRows = tableData.filter((i) => getKeysValue(i) === getKeysValue(record));
      if (recordRows.length === 1) return;

      nextTick(() =>
        tableAction.setSelectedRows(
          selected
            ? uniq(selectedRows.concat(recordRows))
            : selectedRows.filter((row) => !recordRows.find((i) => i.key === row.key)),
        ),
      );
    },
  };
  tableAction.setProps({ rowSelection }, true);
};

export const useDynamicColumns = (
  tableAction: FormTableAction,
  FLAG = 'DYNAMIC' as BasicColumn['flag'],
) => {
  return {
    /** 根据数据生成动态列 */
    createDynamicColumnsByData: (dataSource: Recordable[], option: DynamicOption) =>
      [...dataSource[0][option.dataField]].map((i, index) => ({
        flag: FLAG,
        ...option.defColumn,
        dataIndex: [option.dataField, index, option.dataIndexKey],
        title: i[option.titleKey],
      })),
    /** 获取动态列 */
    getDynamicColumns: (opt?: GetColumnsParams) =>
      tableAction.getColumns(opt).filter((item) => item.flag === FLAG),
    /** 设置动态列 */
    setDynamicColumns: (
      dynamicColumns: BasicColumn[],
      appendAfter?: DynamicOption['appendAfter'],
    ) => {
      if (!dynamicColumns?.length) return;

      const columns = tableAction.getColumns().filter((item) => item.flag !== FLAG);
      const appendIndex = appendAfter ? columns.findIndex((i) => i.dataIndex === appendAfter) : 0;
      const appendColumns = dynamicColumns.map((i) => ({ ...i, flag: i.flag || FLAG }));
      columns.splice(appendIndex, 0, ...appendColumns);
      tableAction.setColumns(columns, true);
    },
  };
};
